import axios, { type AxiosRequestConfig, isAxiosError } from 'axios';
import { env } from '@/shared/config';
import { i18n } from '@/shared/i18n';
import { toast } from '@/shared/lib';
import { ApiError } from './api-error';
import { API_SUCCESS_CODE, type ApiEnvelope } from './types';

// Shared must not depend on entities, so the app layer registers how to read the
// access token and what to do on 401 (see app/setup/api-auth-bridge.ts).
let getAccessToken: () => string | null = () => null;
let onUnauthorized: () => void = () => {};

export function configureApiAuth(options: {
  getAccessToken: () => string | null;
  onUnauthorized: () => void;
}) {
  getAccessToken = options.getAccessToken;
  onUnauthorized = options.onUnauthorized;
}

export interface RequestOptions extends AxiosRequestConfig {
  /** Skip the global error toast (the caller shows its own feedback). */
  silent?: boolean;
}

const http = axios.create({
  baseURL: env.apiBaseUrl,
  timeout: 50_000,
  headers: { 'Content-Type': 'application/json;charset=utf-8' },
});

http.interceptors.request.use((config) => {
  const token = getAccessToken();
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

/**
 * Error handling mirrors the production client's interceptor:
 * - 401 "updated by admin" → info toast, session cleared
 * - 401 otherwise → session cleared (plus error toast)
 * - 400 → no toast; forms show validation messages themselves
 * - everything else → error toast with the server message
 */
function handleHttpError(error: unknown, silent: boolean): never {
  if (!isAxiosError<ApiEnvelope<unknown>>(error)) throw error;
  const status = error.response?.status;
  const serverMessage = error.response?.data?.message;
  const message =
    serverMessage ||
    (error.response ? i18n.t('errors.generic') : i18n.t('errors.network')) ||
    error.message;

  if (status === 401 && serverMessage?.includes('updated by admin')) {
    toast.info(i18n.t('errors.profileUpdatedByAdmin'));
    onUnauthorized();
    throw new ApiError(message, status);
  }
  if (status !== 400 && !silent) toast.error(message);
  if (status === 401) onUnauthorized();
  throw new ApiError(message, status);
}

async function request<T>({ silent = false, ...config }: RequestOptions): Promise<T> {
  let res: Awaited<ReturnType<typeof http.request<ApiEnvelope<T>>>>;
  try {
    res = await http.request<ApiEnvelope<T>>(config);
  } catch (error) {
    handleHttpError(error, silent);
  }
  // Binary downloads (export jobs) are returned as-is.
  if (config.responseType === 'blob') return res.data as unknown as T;
  if (!res.data) {
    const message = i18n.t('errors.requestFailed');
    if (!silent) toast.error(message);
    throw new ApiError(message, res.status);
  }
  if (res.data.statusCode !== API_SUCCESS_CODE) {
    const message = res.data.message || i18n.t('errors.requestFailed');
    if (!silent) toast.error(message);
    throw new ApiError(message, res.status);
  }
  return res.data.data as T;
}

export const apiClient = {
  get: <T>(url: string, config?: RequestOptions) => request<T>({ ...config, method: 'GET', url }),
  post: <T>(url: string, data?: unknown, config?: RequestOptions) =>
    request<T>({ ...config, method: 'POST', url, data }),
  put: <T>(url: string, data?: unknown, config?: RequestOptions) =>
    request<T>({ ...config, method: 'PUT', url, data }),
  patch: <T>(url: string, data?: unknown, config?: RequestOptions) =>
    request<T>({ ...config, method: 'PATCH', url, data }),
  /** `data` is sent as the request body (e.g. DELETE /auth/profile needs the password). */
  delete: <T>(url: string, config?: RequestOptions) =>
    request<T>({ ...config, method: 'DELETE', url }),
  /** Downloads a file (Blob) from an authenticated endpoint. */
  download: (url: string, config?: RequestOptions) =>
    request<Blob>({ ...config, method: 'GET', url, responseType: 'blob' }),
};
